import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from './useInView';
import { HiChevronLeft, HiChevronRight, HiStar } from 'react-icons/hi';

const testimonials = [
  {
    name: 'Sarah M.',
    treatment: 'Lip Enhancement',
    quote:
      'Dr. Noora has the most incredible eye for detail. My lips look naturally full and beautiful — exactly what I wanted. She took the time to understand my goals and the results exceeded my expectations.',
    rating: 5,
  },
  {
    name: 'Fatima K.',
    treatment: 'Facial Contouring',
    quote:
      'I was nervous about fillers, but Dr. Noora made me feel completely at ease. The results are so natural that my friends just say I look "refreshed." That\'s exactly the compliment I was hoping for!',
    rating: 5,
  },
  {
    name: 'Amira R.',
    treatment: 'Skin Rejuvenation',
    quote:
      'After just three sessions, my skin looks like it\'s been airbrushed. Dr. Noora\'s approach is truly bespoke — she created a treatment plan specifically for my skin concerns. Absolutely worth it.',
    rating: 5,
  },
  {
    name: 'Layla H.',
    treatment: 'Botox Treatment',
    quote:
      'The most natural Botox results I\'ve ever had. I can still express myself fully — I just look more rested and youthful. Dr. Noora is a true artist.',
    rating: 5,
  },
];

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const [ref, inView] = useInView({ threshold: 0.2 });

  const next = () => setCurrent((c) => (c + 1) % testimonials.length);
  const prev = () =>
    setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);

  return (
    <section
      id="testimonials"
      ref={ref}
      className="section-padding bg-white"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="gold-line" />
            <span className="text-xs tracking-[0.3em] uppercase text-gold-600 font-medium">
              Testimonials
            </span>
            <span className="gold-line" />
          </div>
          <h2 className="heading-lg">
            What Our Patients{' '}
            <span className="italic text-gold-500">Say</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="relative"
        >
          <div className="bg-cream rounded-2xl p-8 md:p-12 lg:p-16 min-h-[320px] flex items-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="text-center w-full"
              >
                {/* Stars */}
                <div className="flex justify-center gap-1 mb-6">
                  {Array.from({ length: testimonials[current].rating }).map(
                    (_, i) => (
                      <HiStar key={i} className="w-5 h-5 text-gold-400" />
                    )
                  )}
                </div>

                <blockquote className="font-serif text-xl md:text-2xl lg:text-3xl text-charcoal-800 leading-relaxed mb-8 font-normal italic">
                  "{testimonials[current].quote}"
                </blockquote>

                <div>
                  <p className="font-medium text-charcoal-900 tracking-wide">
                    {testimonials[current].name}
                  </p>
                  <p className="text-xs tracking-wider uppercase text-gold-500 mt-1">
                    {testimonials[current].treatment}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-6 mt-8">
            <button
              onClick={prev}
              className="w-12 h-12 rounded-full border border-charcoal-200 flex items-center justify-center hover:bg-charcoal-900 hover:text-white hover:border-charcoal-900 transition-all duration-300"
              aria-label="Previous testimonial"
            >
              <HiChevronLeft size={20} />
            </button>

            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === current
                      ? 'w-8 bg-gold-400'
                      : 'w-1.5 bg-charcoal-200 hover:bg-charcoal-300'
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-12 h-12 rounded-full border border-charcoal-200 flex items-center justify-center hover:bg-charcoal-900 hover:text-white hover:border-charcoal-900 transition-all duration-300"
              aria-label="Next testimonial"
            >
              <HiChevronRight size={20} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
